<template>
  <v-card class="mb-6 whatsapp-file-upload">
    <v-card-text>
      <h3 class="whatsapp-card-title mb-3">Escolha uma imagem ou arquivo em PDF</h3>

      <mf-button :disabled="disabled" :loading="loading" size="sm" label="Selecionar" @click="activateInput" />
      <span v-if="fileProperties && fileProperties.filename" class="file-chip d-flex mt-3" style="line-height: 1">
        <mf-icon fill small color="marca-0">insert_drive_file</mf-icon>
        {{ fileProperties.filename }}
        <mf-icon small class="close-icon" @click="deleteFile">close</mf-icon>
      </span>
      <canvas id="the-canvas" style="display: none" />
      <p class="hint-text mt-3 mb-0">Formatos aceitos: PDF, PNG, JPG ou JPEG. Limite de 10 MB.</p>
      <input ref="file-input" class="hidden-input" type="file" @input="readFile($event)" />
    </v-card-text>
  </v-card>
</template>

<script>
import Swal from 'sweetalert2'
import pdfjs from 'pdfjs-dist/webpack'
import gql from 'graphql-tag'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'

const MUTATION_SINGLE_UPLOAD = gql`
  mutation SingleUpload($file: Upload!) {
    singleUpload(file: $file) {
      filename
      mimetype
      url
    }
  }
`

export default {
  name: 'FileUploadCard',
  props: {
    fileProperties: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    file: null,
    fileName: '',
    fileExtension: '',
    loading: false,
    allowedFileTypes: ['pdf', 'png', 'jpg', 'jpeg'],
  }),
  methods: {
    activateInput() {
      this.$refs['file-input'].value = null
      this.$refs['file-input'].click()
    },
    deleteFile() {
      this.file = null
      this.fileName = ''
      this.fileExtension = ''
      this.$emit('update:fileProperties', {})
    },
    validFormatType(file) {
      return this.allowedFileTypes.includes(file.name.split('.').pop().toLowerCase())
    },
    readFile(event) {
      if (event.target.files.length === 0) return
      this.file = event.target.files[0]

      if (this.file.size > 10000000) {
        Swal.fire({
          text: 'Arquivo com mais de 10mb.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1800,
          onAfterClose: this.goToFocusElement,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else if (!this.validFormatType(this.file)) {
        Swal.fire({
          text: 'Formato de arquivo inválido.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1800,
          onAfterClose: this.goToFocusElement,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else {
        this.fileName = this.file.name
        this.fileExtension = this.file.name.split('.').pop()

        if (this.fileExtension.toLowerCase() === 'pdf') {
          this.pdfUpload({ file: this.file, originalFilename: this.fileName })
        } else {
          this.imageUpload({ file: this.file, originalFilename: this.fileName })
        }
      }
    },
    async pdfUpload({ file, originalFilename }) {
      try {
        this.loading = true
        const base64 = await this.getBase64ImagemFromPdf(this.file)

        const { url: thumbnail_url } = await this.uploadFileBase64(originalFilename.replace('pdf', 'jpg'), base64)
        const { filename, mimetype, url: data_url } = await this.uploadFile(file)

        this.$emit('update:fileProperties', { filename, mimetype, thumbnail_url, data_url })
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async imageUpload({ file, originalFilename }) {
      try {
        this.loading = true
        const { filename, mimetype, url } = await this.uploadFile(this.file)

        this.$emit('update:fileProperties', { filename, mimetype, thumbnail_url: url, data_url: url })
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    uploadFile(file) {
      return this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD,
          variables: { file: this.file },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUpload } }) => {
          const { filename, mimetype, url } = singleUpload
          return { filename, mimetype, url }
        })
    },
    uploadFileBase64(filename, base64) {
      return this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { filename, mimetype, url } = singleUploadBase64
          return { filename, mimetype, url }
        })
    },
    goToFocusElement() {
      const position = this.$refs['file-input'].parentElement.getBoundingClientRect()
      window.scrollTo(0, parseInt(position.top) - 250)
    },
    getBase64ImagemFromPdf(file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.onload = async function () {
          const typedarray = new Uint8Array(this.result)
          const pdf = await pdfjs.getDocument(typedarray).promise
          const page = await pdf.getPage(1)
          const scale = 1.5
          const viewport = page.getViewport({ scale })
          // Prepare canvas using PDF page dimensions
          const canvas = document.getElementById('the-canvas')
          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width
          // Render PDF page into canvas context
          const task = page.render({ canvasContext: context, viewport: viewport })
          await task.promise
          resolve(canvas.toDataURL('image/jpeg'))
        }
        fileReader.readAsArrayBuffer(file)
      })
    },
  },
}
</script>

<style lang="scss">
.whatsapp-file-upload {
  .hidden-input {
    display: none;
  }

  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-d10;
  }

  .file-chip {
    user-select: none;
    border-radius: 4px;
    .close-icon {
      cursor: pointer;
    }
  }
}
</style>
